import React from "react";
import Card from "features/shared/components/card/Card";
import RoundButton from "features/shared/components/buttons/RoundButton";
import CommodityIcon from "features/shared/components/icons/CommodityIcon";
import { formatDate } from "features/shared/helpers/Formatters";
import { ContractProcessDTO } from "../dtos/ContractProcessDTO";
import { getContractProcessDetailRoute } from "features/shared/helpers/routeHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type ContractProcessItem = NonNullable<ContractProcessDTO["result"]>[number];

interface Props { contractProcess: ContractProcessItem; }

const ContractProcessCard = ({ contractProcess }: Props) => {
  // helpers
  const addressText = (() => {
    const a = contractProcess.contractEnergy?.placeOfDeliveryAddress;
    if (!a) return "-";
    return [a.street, a.houseNumber, a.city, a.zipCode].filter(Boolean).join(", ");
  })();

  const dateISO = contractProcess.effectiveDate || "";
  const dateText = dateISO ? formatDate(dateISO) : "-";

  const renderDate = (variant: "inline" | "plain") =>
    variant === "inline" ? (
      <span className="small d-inline-flex align-items-center text-nowrap">
        <FontAwesomeIcon icon="calendar-check" size="xs" fixedWidth />
        <time dateTime={dateISO}>{dateText}</time>
      </span>
    ) : (
      <time dateTime={dateISO}>{dateText}</time>
    );

  const renderProcess = () => (
    <div className="text-truncate" title={contractProcess.processName || "-"}>
      {contractProcess.processName || "-"}
    </div>
  );

  const renderState = () => (
    <div className="d-inline-flex align-items-center text-truncate">
      <span className="d-inline-block rounded-circle p-1 mr-1 bg-info" />
      <small className="text-info text-truncate d-inline-block" title={contractProcess.processState || "-"}>
        {contractProcess.processState || "-"}
      </small>
    </div>
  );

  return (
    <Card className="px-3 py-1 mb-2">
      <div className="row no-gutters align-items-center">
        <div className="col">
          <div className="row no-gutters align-items-center">
            <div className="col-12 col-sm-5 col-md-5 col-lg-4 text-truncate">
              <div className="d-flex align-items-center overflow-hidden" style={{ minWidth: 0 }}>
                <CommodityIcon
                  commodityDetail={contractProcess.commodityDetail!}
                  includeColor
                  size="lg"
                />
                <div className="ml-2 overflow-hidden" style={{ minWidth: 0 }}>
                  <div className="text-truncate" title={contractProcess.contractEnergy?.placeOfDelivery || "-"}>
                    {contractProcess.contractEnergy?.placeOfDelivery || "-"}
                  </div>
                  <div className="small text-muted text-truncate" title={addressText}>
                    {addressText}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-7 col-md-7 col-lg-8">
              <div className="row no-gutters">
                <div className="col-12 col-sm-6 col-md-5 col-lg-6">
                  <div className="row no-gutters align-items-center">
                    <div className="col-auto col-sm-12 col-xl-6 pr-3 text-truncate">
                      <div className="text-muted text-truncate" title={contractProcess.contractNumber || "-"}>
                        {contractProcess.contractNumber || "-"}
                      </div>
                    </div>
                    <div className="col-6 col-sm-12 col-xl-6">
                      <span className="d-xl-none">{renderDate("inline")}</span>
                      <span className="d-none d-xl-inline">{renderDate("plain")}</span>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-md-7 col-lg-6">
                  <div className="row no-gutters align-items-start">
                    <div className="col-auto col-sm-12 col-xl-6 pr-3">
                      {renderProcess()}
                    </div>
                    <div className="col-6 col-sm-12 col-xl-6">
                      <div className="ml-lg-2 ml-xl-0">{renderState()}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-auto pl-2 pl-lg-3">
          <RoundButton asLink to={getContractProcessDetailRoute(contractProcess.contractProcessId!)} />
        </div>
      </div>
    </Card>
  );
};

export default ContractProcessCard;
